import { Divider, Empty, IconFont } from '@byecode/ui'
import { useVirtualizer } from '@tanstack/react-virtual'
import * as React from 'react'
import { useCallback, useMemo, useRef } from 'react'

import { CURRENT_USER } from '../../constants'
import type { RoleOption, UserOption } from '../../types'
import { SelectItemPreviewer } from './SelectItemPreviewer'
import * as SC from './styles'

export type PersonItemExtra = {
    role?: string
    department?: string
}

interface DropDownListProps {
    options: UserOption[]
    roleOptions?: RoleOption[]
    rightProperty?: 'roleName' | 'userId'
    enableCheckBox?: boolean
    color?: string
    value?: string[]
    emptyDesc?: string
    searchWord?: string
    extraComponent?: React.FC<{ onClose?: () => void }>
    enableCurrentUser?: boolean
    currentValue?: string[]
    getItemExtra?: (user: UserOption) => PersonItemExtra
    onClose?: () => void
    onMultipleChange: (userId: string) => void
}

export const DropDownList: React.FunctionComponent<DropDownListProps> = ({
    options: currentOptions,
    rightProperty,
    roleOptions,
    color,
    value,
    enableCheckBox,
    enableCurrentUser,
    emptyDesc,
    searchWord,
    extraComponent,
    getItemExtra,
    onClose,
    onMultipleChange
}) => {
    const scrollYRef = useRef<HTMLDivElement>(null)
    // 部门有无决定行高。stamp 变化时重建 getItemKey，虚拟列表才会按新行高重算偏移。
    const departmentStamp = useMemo(() => {
        if (!getItemExtra) {
            return ''
        }
        let stamp = ''
        for (const option of currentOptions) {
            if (getItemExtra(option).department) {
                stamp += option.id
                stamp += '\n'
            }
        }
        return stamp
    }, [currentOptions, getItemExtra])
    const getItemKey = useCallback(
        (index: number) => currentOptions[index]?.id ?? index,
        [currentOptions, departmentStamp]
    )
    const rowVirtualizer = useVirtualizer({
        count: currentOptions.length,
        paddingStart: 0,
        getScrollElement: () => scrollYRef.current,
        estimateSize: index => {
            const option = currentOptions[index]
            if (!getItemExtra || !option || !getItemExtra(option).department) {
                return SC.PERSON_ROW_HEIGHT
            }
            return SC.PERSON_ROW_HEIGHT_WITH_DEPARTMENT
        },
        getItemKey: getItemExtra ? getItemKey : undefined
    })
    const isEmptyOptions = useMemo(() => currentOptions.length === 0, [currentOptions.length])

    const getExtraText = useCallback(
        (_data: UserOption) => {
            return ''
        },
        [rightProperty, roleOptions]
    )
    return (
        <>
            {searchWord === '' && (
                <SC.Header>
                    {extraComponent?.({ onClose })}
                    {enableCurrentUser && (
                        <>
                            <SC.DropDownItem onClick={() => onMultipleChange(CURRENT_USER.id)}>
                                <SelectItemPreviewer data={CURRENT_USER} value={value} color={color} enableCheckBox={enableCheckBox} />
                            </SC.DropDownItem>
                            <Divider mt={8} />
                        </>
                    )}
                </SC.Header>
            )}
            <SC.List ref={scrollYRef} pt={searchWord === '' ? 8 : 0}>
                <div
                    style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: '100%',
                        position: 'relative'
                    }}
                >
                    {rowVirtualizer.getVirtualItems().map(item => {
                        const { index, start } = item
                        const option = currentOptions[index]
                        const extra = getItemExtra?.(option)
                        const department = extra?.department || undefined
                        return (
                            <SC.DropDownItem
                                key={option.id}
                                $grow={Boolean(department)}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    transform: `translateY(${start}px)`
                                }}
                                onClick={() => onMultipleChange(option.id)}
                            >
                                <SelectItemPreviewer
                                    data={option}
                                    color={color}
                                    extraText={extra ? extra.role || undefined : getExtraText(option)}
                                    department={department}
                                    value={value}
                                    enableCheckBox={enableCheckBox}
                                />
                            </SC.DropDownItem>
                        )
                    })}
                </div>
                {isEmptyOptions && (
                    <Empty
                        styles={{
                            root: {
                                height: 110
                            }
                        }}
                        icon={<IconFont type="User" size={16} color="var(--color-gray-400)" />}
                        description={emptyDesc}
                    />
                )}
            </SC.List>
        </>
    )
}
